import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Button
} from "@mui/material";
import {
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Info as InfoIcon,
  Phone as PhoneIcon,
} from "@mui/icons-material";
import tenantUsersService from "../services/tenantUsersService";
// tenantService removed — we only show tenants related to the current user
import Logo from "../assets/ZakazujMeLogo4.png";
import { useSelector, useDispatch } from "react-redux";
import { setSelectedTenantForCustomer } from "../store/slices/authSlice";
// lookupService removed — client-type filters removed
import { useNavigate } from "react-router-dom";
import lookupService from "../services/lookupService";

const accentColors = [
  "linear-gradient(135deg, #7C3AED 0%, #06B6D4 100%)",
  "linear-gradient(135deg, #F59E42 0%, #F43F5E 100%)",
  "linear-gradient(135deg, #22D3EE 0%, #6366F1 100%)",
  "linear-gradient(135deg, #34D399 0%, #FBBF24 100%)",
];

const TenantCard = ({ tenant, accent, isBlurred, showRegisterButton, onRegisterClick, onCardClick, registerStatus, showReserveButton, onReserveClick, onlyReserve, onOpenClick, clientTypes, requiresApproval }) => (
  <Box
    id={`tenant-card-${tenant._id}`}
    sx={{
      background: "rgba(255,255,255,0.12)",
      backdropFilter: "blur(8px)",
      borderRadius: 4,
      boxShadow: "0 4px 24px rgba(124,58,237,0.10)",
      border: "2px solid transparent",
      p: 0, // Ukloni padding
      minHeight: 240,
      display: "flex",
      flexDirection: "row", // Horizontalno
      overflow: "hidden",
      transition: "transform .22s, box-shadow .22s, border-color .22s",
      position: "relative",
      cursor: isBlurred ? "default" : "pointer",
      '&:hover': !isBlurred ? {
        transform: "translateY(-6px) scale(1.03)",
        boxShadow: "0 8px 32px rgba(124,58,237,0.18)",
        border: `2px solid ${accent}`,
      } : {},
    }}
    onClick={onCardClick}
  >
    {/* Leva polovina - Logo */}
    <Box
      sx={{
        width: "50%",
        background: tenant.logoUrl 
          ? `url(${tenant.logoUrl})`
          : "linear-gradient(135deg, #7C3AED20 0%, #06B6D420 100%)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "16px 0 0 16px",
      }}
    >
      {/* Overlay za logo deo */}
      {tenant.logoUrl && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "linear-gradient(rgba(30,41,59,0.3), rgba(30,41,59,0.4))",
            borderRadius: "inherit",
          }}
        />
      )}
      {/* Ako nema logo, prikaži ikonu */}
      {!tenant.logoUrl && (
        <BusinessIcon sx={{ fontSize: 60, color: "#7C3AED", opacity: 0.6, zIndex: 1 }} />
      )}
    </Box>

    {/* Desna polovina - Podaci */}
    <Box
      sx={{
        width: "50%",
        p: 2,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        position: "relative",
        zIndex: 2,
      }}
    >
      {/* Sadržaj kartice */}
      <Box sx={{ 
        opacity: isBlurred ? 0.7 : 1,
        pointerEvents: isBlurred ? "none" : "auto",
      }}>
        <Box sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{
            fontWeight: 800,
            letterSpacing: "-0.02em",
            color: "#fff",
            textShadow: "0 2px 8px #7C3AED44",
            fontSize: "1rem",
          }}>
            {tenant.name}
          </Typography>
        </Box>
        <Box sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
          <LocationIcon sx={{ fontSize: 16, color: "#06B6D4" }} />
          <Typography variant="body2" sx={{ color: "#e0e7ff", fontSize: "0.75rem" }}>
            {tenant.address?.street} {tenant.address?.number}, {tenant.address?.city}
          </Typography>
        </Box>
        <Box sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
          <InfoIcon sx={{ fontSize: 16, color: "#F59E42" }} />
          <Typography variant="body2" sx={{ color: "#fbbf24", fontSize: "0.75rem" }}>
            {(() => {
              const typeObj = clientTypes?.find(ct => ct._id === tenant.clientType);
              return typeObj?.name || tenant.clientTypeName || tenant.clientType;
            })()}
          </Typography>
        </Box>
        {tenant.phone && (
          <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneIcon sx={{ fontSize: 16, color: '#22D3EE' }} />
            <Typography variant="body2" sx={{ color: '#e0e7ff', fontSize: "0.75rem" }}>
              {tenant.phone}
            </Typography>
          </Box>
        )}
        <Typography variant="body2" sx={{ color: "#cbd5e1", fontSize: "0.7rem", lineHeight: 1.3 }}>
          {tenant.description?.length > 60 ? `${tenant.description.substring(0, 60)}...` : tenant.description}
        </Typography>
      </Box>
    </Box>
    
    {/* Blur overlay preko cele kartice kad je isBlurred */}
    {/* Blur overlay preko cele kartice kad je isBlurred */}
    {isBlurred && (
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 10, // Najviši z-index da bude preko svega
          backdropFilter: "blur(3px)",
          background: "rgba(30,41,59,0.08)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          borderRadius: "inherit",
        }}
        onClick={e => e.stopPropagation()}
      >
        {registerStatus === 'success' && (
          <Typography variant="h6" sx={{ color: '#22D3EE', mb: 2, fontWeight: 700 }}>
            Uspešno ste se registrovali!
          </Typography>
        )}
        {registerStatus === 'approval-pending' && (
          <Typography variant="h6" sx={{ color: '#F59E42', mb: 2, fontWeight: 700 }}>
            Uspešno ste poslali zahtev za registraciju!
          </Typography>
        )}
        {registerStatus === 'error' && (
          <Typography variant="h6" sx={{ color: '#F43F5E', mb: 2, fontWeight: 700 }}>
            Greška prilikom registracije!
          </Typography>
        )}
        {!registerStatus && showReserveButton && (
          <>
            <Button
              variant="contained"
              sx={{
                borderRadius: 999,
                px: 4,
                py: 1,
                fontWeight: 700,
                background: 'linear-gradient(90deg, #22D3EE 0%, #6366F1 100%)',
                color: 'white',
                boxShadow: '0 2px 8px #6366F144',
                fontSize: 18,
                mb: 2,
                '&:hover': {
                  background: 'linear-gradient(90deg, #6366F1 0%, #22D3EE 100%)',
                },
              }}
              onClick={onOpenClick}
            >
              Otvori
            </Button>
            <Button
              variant="contained"
              sx={{
                borderRadius: 999,
                px: 4,
                py: 1,
                fontWeight: 700,
                background: 'linear-gradient(90deg, #7C3AED 0%, #06B6D4 100%)',
                color: 'white',
                boxShadow: '0 2px 8px #7C3AED44',
                fontSize: 18,
                mb: 2,
                '&:hover': {
                  background: 'linear-gradient(90deg, #06B6D4 0%, #7C3AED 100%)',
                },
              }}
              onClick={onReserveClick}
            >
              Rezerviši termin
            </Button>
            <Button
              variant="outlined"
              sx={{
                borderRadius: 999,
                px: 4,
                py: 1,
                fontWeight: 700,
                color: '#fff',
                borderColor: '#6366F1',
                fontSize: 16,
                '&:hover': {
                  background: 'rgba(99,102,241,0.08)',
                  borderColor: '#22D3EE',
                },
              }}
              onClick={onRegisterClick}
            >
              Registruj se
            </Button>
            {requiresApproval && (
              <Typography variant="body2" sx={{ 
                color: '#F59E42', 
                fontSize: '0.75rem', 
                textAlign: 'center', 
                mt: 1, 
                fontWeight: 600 
              }}>
                Na ovom klijentu morate sačekati odobrenje za registraciju
              </Typography>
            )}
          </>
        )}
        {!registerStatus && onlyReserve && (
          <>
            <Button
              variant="contained"
              sx={{
                borderRadius: 999,
                px: 4,
                py: 1,
                fontWeight: 700,
                background: 'linear-gradient(90deg, #22D3EE 0%, #6366F1 100%)',
                color: 'white',
                boxShadow: '0 2px 8px #6366F144',
                fontSize: 18,
                mb: 2,
                '&:hover': {
                  background: 'linear-gradient(90deg, #6366F1 0%, #22D3EE 100%)',
                },
              }}
              onClick={onOpenClick}
            >
              Otvori
            </Button>
            <Button
              variant="contained"
              sx={{
                borderRadius: 999,
                px: 4,
                py: 1,
                fontWeight: 700,
                background: 'linear-gradient(90deg, #7C3AED 0%, #06B6D4 100%)',
                color: 'white',
                boxShadow: '0 2px 8px #7C3AED44',
                fontSize: 18,
                '&:hover': {
                  background: 'linear-gradient(90deg, #06B6D4 0%, #7C3AED 100%)',
                },
              }}
              onClick={onReserveClick}
            >
              Rezerviši termin
            </Button>
          </>
        )}
        {!registerStatus && !showReserveButton && !onlyReserve && (
          <>
            <Button
              variant="contained"
              sx={{
                borderRadius: 999,
                px: 4,
                py: 1,
                fontWeight: 700,
                background: 'linear-gradient(90deg, #22D3EE 0%, #6366F1 100%)',
                color: 'white',
                boxShadow: '0 2px 8px #6366F144',
                fontSize: 18,
                '&:hover': {
                  background: 'linear-gradient(90deg, #6366F1 0%, #22D3EE 100%)',
                },
              }}
              onClick={onRegisterClick}
            >
              Registruj se
            </Button>
            {requiresApproval && (
              <Typography variant="body2" sx={{ 
                color: '#F59E42', 
                fontSize: '0.75rem', 
                textAlign: 'center', 
                mt: 1, 
                fontWeight: 600 
              }}>
                Na ovom klijentu morate sačekati odobrenje za registraciju
              </Typography>
            )}
          </>
        )}
      </Box>
    )}
  </Box>
);

const Homepage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [blurredTenantId, setBlurredTenantId] = useState(null);
  const [tenantsForUser, setTenantsForUser] = useState([]);
  const [clientTypes, setClientTypes] = useState([]);
  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Pozovi backend sa samo userId - prikazujemo samo tenante koji su vezani za korisnika
        const tenantsForUserResponse = await tenantUsersService.getTenantsForUser({
          userId: user?._id,
        });
        console.log('tenantsForUserResponse', tenantsForUserResponse);
        // ne prikazivati one koji imaju vezu sa tenantom a deaktivirani su
        const usersTenants = (tenantsForUserResponse || []).filter(
          (o) => o.approved === true
        );
        setTenantsForUser(usersTenants || []);
      } catch (e) {
        setTenantsForUser([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  // console.log('registrationTenants', registrationTenants);
  // Dodaj handler za klik van kartice
  useEffect(() => {
    if (!blurredTenantId) return;
    const handleClickOutside = (e) => {
      // Proveri da li je kliknuto na karticu ili overlay
      const card = document.getElementById(`tenant-card-${blurredTenantId}`);
      if (card && !card.contains(e.target)) {
        setBlurredTenantId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [blurredTenantId]);

  useEffect(() => {
    const fetchClientTypes = async () => {
      try {
        const response = await lookupService.getAllLookupValues('_tip_klijenta');
        setClientTypes(response || []);
      } catch (e) {
        setClientTypes([]);
      }
    };
    fetchClientTypes();
  }, []);
  

  // Handler for otvori - preusmeri na dashboard
  const handleOpenClick = (tenant) => {
    // Postavi tenantId u Redux store kao da customer ima jednu konekciju
    dispatch(setSelectedTenantForCustomer(tenant._id));
    
    // Preusmeri na dashboard
    navigate('/dashboard');
  };

  // Handler for rezervisi termin - preusmeri na appointments i otvori modal
  const handleReserveClick = (tenant) => {
    // Postavi tenantId u Redux store kao da customer ima jednu konekciju
    dispatch(setSelectedTenantForCustomer(tenant._id));
    
    // Preusmeri na appointments sa flagom da se otvori modal
    navigate('/appointments', { state: { openNewAppointment: true } });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, width: '100%', position: 'relative', minHeight: '100vh' }}>
      {/* Logo na sredini, veći */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Box component="img" src={Logo} alt="Zakazuj Logo" sx={{ height: { xs: 110, md: 150 }, objectFit: 'contain' }} />
      </Box>
      {/* Tabs and client-type filters removed — show only tenants from tenantsForUser */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Show only tenants related to the user */}
          <Grid container spacing={4}>
            {tenantsForUser.map((tenant, i) => (
              <Grid item xs={12} sm={6} md={3} key={tenant._id}>
                <TenantCard
                  tenant={tenant}
                  accent={accentColors[i % accentColors.length]}
                  isBlurred={blurredTenantId === tenant._id}
                  onlyReserve={tenant.approved !== false}
                  onReserveClick={() => handleReserveClick(tenant)}
                  onOpenClick={() => handleOpenClick(tenant)}
                  onCardClick={() => !blurredTenantId && setBlurredTenantId(tenant._id)}
                  clientTypes={clientTypes}
                />
              </Grid>
            ))}
            {tenantsForUser.length === 0 && (
              <Grid item xs={12}>
                <Typography textAlign="center" color="#fff" mt={6}>
                  Nema pronađenih rezultata za ovu sekciju.
                </Typography>
              </Grid>
            )}
          </Grid>
        </>
      )}
    </Box>
  );
};

export default Homepage;